import { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import OnboardingChecklist from "./OnboardingChecklist";

const Brand = () => (
  <Link to="/app" style={{display:'flex', alignItems:'center', gap:10, textDecoration:'none', color:'inherit'}}>
    <div className="brand-mark">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s4-8 10-8 10 8 10 8-4 8-10 8-10-8-10-8z"/><circle cx="12" cy="12" r="3"/></svg>
    </div>
    <div>
      <div className="brand-title">Gestion <em>Lagon</em></div>
    </div>
  </Link>
);

function initials(name) {
  if (!name) return "?";
  return name.trim().split(/\s+/).slice(0,2).map(x=>x[0]).join("").toUpperCase();
}

const PlanNames = { discovery: "Découverte", solo: "Solo", pro: "Pro", business: "Business" };

export default function AppShell({ children }) {
  const { user, org, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const nav = useNavigate();
  const menuRef = useRef();

  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, []);

  const roleLabels = { owner: "Propriétaire", admin: "Administrateur", accountant: "Comptable", sales: "Commercial", viewer: "Lecture seule" };

  const onLogout = async () => {
    await logout();
    nav("/");
  };

  // Trial backend (no CB) — first 14 days after signup, plan=discovery
  const isBackendTrial = org?.plan === "discovery" && org?.trial_ends_at && new Date(org.trial_ends_at) > new Date();
  const backendTrialDays = isBackendTrial ? Math.max(0, Math.ceil((new Date(org.trial_ends_at) - new Date()) / 86400000)) : 0;
  const backendTrialDayCurrent = isBackendTrial ? Math.min(14, Math.max(1, 14 - backendTrialDays + 1)) : 0;
  const backendTrialPct = isBackendTrial ? Math.round((backendTrialDayCurrent / 14) * 100) : 0;
  const backendTrialColor = backendTrialDays <= 3 ? 'var(--danger)' : backendTrialDays <= 7 ? 'var(--warn)' : 'var(--ok)';

  // Trial Stripe natif (CB enregistrée, débit auto à J+14)
  const isStripeTrial = org?.subscription_status === "trialing" && org?.plan_expires_at;
  const stripeTrialDays = isStripeTrial ? Math.max(0, Math.ceil((new Date(org.plan_expires_at) - new Date()) / 86400000)) : 0;
  const stripeTrialColor = stripeTrialDays <= 3 ? 'var(--danger)' : stripeTrialDays <= 7 ? 'var(--warn)' : 'var(--ok)';

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header-inner">
          <Brand />
          <nav className="app-nav-tabs">
            <NavLink to="/app" end data-testid="tab-gestion">Gestion</NavLink>
            <NavLink to="/app/team" data-testid="tab-team">Équipe</NavLink>
            <NavLink to="/app/billing" data-testid="tab-billing">Abonnement</NavLink>
            <NavLink to="/app/audit" data-testid="tab-audit">Journal</NavLink>
          </nav>
          <div className="user-menu" ref={menuRef}>
            <button className="user-btn" onClick={()=>setMenuOpen(!menuOpen)} data-testid="user-menu-btn">
              <div className="avatar">{initials(user?.name)}</div>
              <div>
                <div className="u-name">{user?.name}</div>
                <div style={{fontSize:11, color:'var(--ink-muted)', lineHeight:1}}>{org?.name}</div>
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color:'var(--ink-muted)', marginLeft:4}}><path d="m6 9 6 6 6-6"/></svg>
            </button>
            <div className={`user-menu-drop ${menuOpen?"":""}`} style={{display: menuOpen?'block':'none'}}>
              <div className="label">Compte</div>
              <div style={{padding:'4px 12px 8px', fontSize:13}}>
                <div style={{fontWeight:500}}>{user?.email}</div>
                <div style={{marginTop:6, display:'flex', gap:6, flexWrap:'wrap'}}>
                  <span className={`status-chip ${user?.role}`}>{roleLabels[user?.role]}</span>
                  <span className="plan-badge">Plan {PlanNames[org?.plan]}</span>
                  {isStripeTrial && (
                    <span className="plan-badge" style={{background:'var(--surface-2)', color:stripeTrialColor, fontWeight:600}} data-testid="trial-pill">
                      Essai · J-{stripeTrialDays}
                    </span>
                  )}
                  {isBackendTrial && !isStripeTrial && (
                    <span className="plan-badge" style={{background:'var(--surface-2)', color:backendTrialColor, fontWeight:600}} data-testid="backend-trial-pill">
                      Essai · J{backendTrialDayCurrent}/14
                    </span>
                  )}
                  {org?.subscription_status === "past_due" && (
                    <span className="plan-badge" style={{background:'var(--surface-2)', color:'var(--danger)', fontWeight:600}} data-testid="past-due-pill">
                      Paiement échoué
                    </span>
                  )}
                </div>
                {isBackendTrial && !isStripeTrial && (
                  <div data-testid="backend-trial-progress" style={{marginTop:10}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:11, color:'var(--ink-muted)', marginBottom:4}}>
                      <span>Jour <strong style={{color:'var(--ink)'}}>{backendTrialDayCurrent}</strong> sur 14</span>
                      <span style={{color:backendTrialColor, fontWeight:600}}>{backendTrialDays} j restant{backendTrialDays>1?"s":""}</span>
                    </div>
                    <div style={{height:3, background:'var(--surface-2)', borderRadius:2, overflow:'hidden'}}>
                      <div style={{height:'100%', width:`${backendTrialPct}%`, background:`linear-gradient(90deg, var(--primary), ${backendTrialColor})`, transition:'width 400ms'}}></div>
                    </div>
                  </div>
                )}
              </div>
              <div className="sep"></div>
              <Link to="/app/billing" onClick={()=>setMenuOpen(false)} data-testid="menu-billing">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                Mon abonnement
              </Link>
              <Link to="/app/team" onClick={()=>setMenuOpen(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                Équipe
              </Link>
              <div className="sep"></div>
              <button onClick={onLogout} data-testid="menu-logout">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      </header>

      <main style={{flex:1}}>
        <OnboardingChecklist />
        {isStripeTrial && stripeTrialDays > 0 && (
          <div style={{maxWidth:1200, margin:'20px auto 0', padding:'0 24px'}}>
            <div className="trial-banner" data-testid="stripe-trial-banner" style={{borderLeft:`3px solid ${stripeTrialColor}`}}>
              <span style={{display:'flex', alignItems:'center', gap:8}}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={stripeTrialColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                <span>
                  <strong>Essai {PlanNames[org?.plan] || "Pro"}</strong>
                  {" · "}encore <strong style={{color:stripeTrialColor}}>{stripeTrialDays} jour{stripeTrialDays>1?"s":""}</strong>
                  {" · aucun débit avant la fin de l'essai · "}
                  {org?.cancel_at_period_end ? (
                    <span style={{color:'var(--ink-muted)'}}>annulation programmée</span>
                  ) : (
                    <span style={{color:'var(--ink-muted)'}}>annulable à tout moment</span>
                  )}
                </span>
              </span>
              <Link to="/app/billing" className="btn btn-ghost btn-sm" style={{marginLeft:'auto'}} data-testid="stripe-trial-manage-btn">Gérer mon abonnement</Link>
            </div>
          </div>
        )}
        {isBackendTrial && backendTrialDays > 0 && !isStripeTrial && (
          <div style={{maxWidth:1200, margin:'20px auto 0', padding:'0 24px'}}>
            <div className="trial-banner" data-testid="trial-banner">
              <span>✨ <strong>Essai Pro gratuit</strong> — encore <strong>{backendTrialDays} jour{backendTrialDays>1?"s":""}</strong> avec toutes les fonctionnalités.</span>
              <Link to="/app/billing" className="btn btn-primary btn-sm" style={{marginLeft:'auto'}}>Choisir mon plan</Link>
            </div>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
